import React, {
  useEffect,
  useRef,
  useCallback,
  memo,
  useState,
  useMemo,
} from 'react';
import OLMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import { fromLonLat } from 'ol/proj';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { Style, Icon, Stroke, Fill, Circle, Text } from 'ol/style';
import { easeOut } from 'ol/easing';
import { debounce } from 'lodash';

// IMPORTANT: ensure OpenLayers css is available globally or via CDN

// Routing providers with fallback chain
interface RouteProvider {
  name: string;
  fetch: (
    start: [number, number],
    end: [number, number],
  ) => Promise<number[][] | null>;
  priority: number;
}

const decodePolyline = (encoded: string): number[][] | null => {
  if (!encoded || typeof encoded !== 'string') return null;

  let index = 0;
  let lat = 0;
  let lng = 0;
  const coordinates: number[][] = [];
  const factor = 1e5;

  try {
    while (index < encoded.length) {
      let b: number;
      let shift = 0;
      let result = 0;

      do {
        if (index >= encoded.length) break;
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlat = (result & 1) ? ~(result >> 1) : result >> 1;
      lat += dlat;

      shift = 0;
      result = 0;

      do {
        if (index >= encoded.length) break;
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlng = (result & 1) ? ~(result >> 1) : result >> 1;
      lng += dlng;

      coordinates.push([lng / factor, lat / factor]);
    }

    return coordinates.length >= 2 ? coordinates : null;
  } catch (error) {
    console.error('Polyline decode error:', error);
    return null;
  }
};

const osrmProvider: RouteProvider = {
  name: 'OSRM',
  priority: 1,
  fetch: async (start, end) => {
    const url = `https://router.project-osrm.org/route/v1/driving/${start[0]},${start[1]};${end[0]},${end[1]}?overview=full&geometries=polyline`;
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data.code === 'Ok' && data.routes?.[0]?.geometry) {
      return decodePolyline(data.routes[0].geometry);
    }
    return null;
  },
};

const graphhopperProvider: RouteProvider = {
  name: 'GraphHopper',
  priority: 2,
  fetch: async (start, end) => {
    const apiKey = import.meta.env.VITE_GRAPHHOPPER_API_KEY;
    if (!apiKey) {
      // console.warn('Skipping GraphHopper: No API key configured (VITE_GRAPHHOPPER_API_KEY)');
      return null;
    }
    const url = `https://graphhopper.com/api/1/route?point=${start[1]},${start[0]}&point=${end[1]},${end[0]}&vehicle=car&locale=en&points_encoded=false&key=${apiKey}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data.paths?.[0]?.points?.coordinates) {
      // GraphHopper returns [lng, lat] (GeoJSON standard) when points_encoded=false
      // Wait, graphhopper returns [lon, lat] for points_encoded=false.
      return data.paths[0].points.coordinates as number[][];
    }
    return null;
  },
};

const PROVIDERS = [osrmProvider, graphhopperProvider];
const routeCache = new Map<string, { coords: number[][]; timestamp: number }>();
const CACHE_TTL = 10 * 60 * 1000;

const fetchRouteWithFallback = async (
  start: [number, number],
  end: [number, number],
  onProviderChange?: (name: string) => void,
): Promise<{ coords: number[][]; provider: string } | null> => {
  const cacheKey = `${start[0]},${start[1]}-${end[0]},${end[1]}`;
  const cached = routeCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    console.log('/ Route cache hit');
    return { coords: cached.coords, provider: 'cache' };
  }

  for (const provider of PROVIDERS) {
    try {
      console.log(`Attempting ${provider.name} ...`);
      onProviderChange?.(provider.name);
      const coords = await provider.fetch(start, end);

      if (coords && coords.length > 2) {
        console.log(`/ ${provider.name} success: ${coords.length} points`);
        routeCache.set(cacheKey, { coords, timestamp: Date.now() });
        return { coords, provider: provider.name };
      }
    } catch (error: any) {
      console.warn(`X ${provider.name} failed:`, error.message);
      continue;
    }
  }

  console.error('All route providers failed');
  return null;
};

// Error boundary
class MapErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Map Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            width: '100%',
            height: '400px',
            background:
              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#fff',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🗺️</div>
          <h3 style={{ margin: '0 0 10px 0' }}>Map Loading Failed</h3>
          <p style={{ margin: '0 0 20px 0', opacity: 0.9 }}>
            {this.state.error?.message || 'Unknown error'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// LiveMap component
interface LiveMapProps {
  vehicles?: { id: string; lat: number; lng: number; label?: string }[];
  pickup?: { lat: number; lng: number } | null;
  delivery?: { lat: number; lng: number } | null;
  height?: string;
  zoom?: number;
  darkMode?: boolean;
  showRealRoute?: boolean;
  animateRoute?: boolean;
  center?: { lat: number; lng: number }; // Optional override
}

const LiveMap: React.FC<LiveMapProps> = memo(
  ({
    vehicles = [],
    pickup,
    delivery,
    height = '500px',
    zoom = 8,
    darkMode = false,
    showRealRoute = true,
    animateRoute = true,
    center,
  }) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<OLMap | null>(null);
    const routeFeatureRef = useRef<Feature<LineString> | null>(null);
    const routeSourceRef = useRef<VectorSource>(new VectorSource());
    const markersSourceRef = useRef<VectorSource>(new VectorSource());
    const vehicleFeatureMapRef = useRef<Map<string, Feature<Point>>>(new Map());

    const [loading, setLoading] = useState(true);
    const [routeStatus, setRouteStatus] = useState<{
      loading: boolean;
      provider: string;
      distance?: string;
      duration?: string;
      error?: string;
    }>({ loading: false, provider: '' });

    const tileSource = useMemo(() => {
      if (darkMode) {
        return new XYZ({
          url: 'https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
          attributions: '@CARTO @OpenStreetMap',
        });
      }
      return new OSM();
    }, [darkMode]);

    // Generate unique color from string (Driver ID)
    const stringToColor = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const c = (hash & 0x00ffffff).toString(16).toUpperCase();
      return '#' + '00000'.substring(0, 6 - c.length) + c;
    };

    // Custom Marker SVG Generator (Modern Pin)
    const getMarkerSVG = (color: string) => {
      // Sleek Pin Design
      const encoded = btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24">
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#000" flood-opacity="0.3"/>
          </filter>
          <g filter="url(#shadow)">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${color}" stroke="white" stroke-width="1.5"/>
            <circle cx="12" cy="9" r="3.5" fill="white"/>
          </g>
        </svg>`);
      return `data:image/svg+xml;base64,${encoded}`;
    };

    // Dynamic Style Cache
    const styleCache = useRef<Map<string, Style>>(new Map());

    const getVehicleStyle = useCallback((feature: Feature<Point>) => {
      const id = feature.getId() as string;
      if (!id) return new Style(); // Fallback

      if (styleCache.current.has(id)) return styleCache.current.get(id);

      const color = stringToColor(id);
      const style = new Style({
        image: new Icon({
          src: getMarkerSVG(color), // Modern Pin
          scale: 0.85, // Slightly larger for visibility
          anchor: [0.5, 1], // Anchor at bottom tip
        }),
        text: new Text({
          text: '🚚',
          scale: 0.9,
          offsetY: -32 // Position icon inside/above pin
        })
      });

      styleCache.current.set(id, style as Style);
      return style;
    }, []);

    const pickupStyle = useMemo(
      () =>
        new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({ color: '#16a34a' }), // Green
            stroke: new Stroke({ color: '#fff', width: 2.5 }),
          }),
        }),
      [],
    );

    const deliveryStyle = useMemo(
      () =>
        new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({ color: '#dc2626' }), // Red
            stroke: new Stroke({ color: '#fff', width: 2.5 }),
          }),
        }),
      [],
    );

    const routeStyle = useMemo(
      () =>
        new Style({
          stroke: new Stroke({
            color: darkMode ? '#38bdf8' : '#0284c7', // Sky blue
            width: 6,
            lineCap: 'round',
            lineJoin: 'round',
          }),
        }),
      [darkMode],
    );

    // Initial Center Calculation
    const initialCenter = useMemo(() => {
      if (center) return fromLonLat([center.lng, center.lat]);
      if (vehicles.length > 0) return fromLonLat([vehicles[0].lng, vehicles[0].lat]);
      if (pickup) return fromLonLat([pickup.lng, pickup.lat]);
      return fromLonLat([33.78, -13.96]); // Malawi default
    }, [center, vehicles, pickup]); // Initial calculation dependencies are fine


    const updateRealRoute = useCallback(async () => {
      if (!mapRef.current) return;

      // If routing disabled or missing endpoints, draw straight line/fallback
      if (!showRealRoute || !pickup || !delivery) {
        if (pickup && delivery) {
          const coords = [
            fromLonLat([pickup.lng, pickup.lat]),
            fromLonLat([delivery.lng, delivery.lat]),
          ];
          if (routeFeatureRef.current) {
            (routeFeatureRef.current.getGeometry() as LineString).setCoordinates(coords);
          } else {
            routeFeatureRef.current = new Feature(new LineString(coords));
            routeSourceRef.current.addFeature(routeFeatureRef.current);
          }
        }
        return;
      }

      setRouteStatus({ loading: true, provider: 'Initializing ...' });
      // Only clear if we are re-fetching
      if (!routeFeatureRef.current) {
        routeSourceRef.current.clear();
      }

      const result = await fetchRouteWithFallback(
        [pickup.lng, pickup.lat],
        [delivery.lng, delivery.lat],
        (provider) =>
          setRouteStatus((prev) => ({ ...prev, provider: provider || '' })),
      );

      if (result && result.coords.length > 2) {
        // result coords likely [lng, lat]
        const olCoords = result.coords.map((c) => fromLonLat([c[0], c[1]]));

        if (routeFeatureRef.current) {
          (routeFeatureRef.current.getGeometry() as LineString).setCoordinates(olCoords);
        } else {
          routeFeatureRef.current = new Feature(new LineString(olCoords));
          routeFeatureRef.current.setStyle(routeStyle);
          routeSourceRef.current.addFeature(routeFeatureRef.current);
        }

        const line = new LineString(olCoords);
        const length = line.getLength();
        const distanceKm = (length / 1000).toFixed(1);

        setRouteStatus({
          loading: false,
          provider: result.provider,
          distance: `${distanceKm} km`,
          duration: `~${Math.ceil(parseFloat(distanceKm) / 40 * 60)} min`,
        });

        const extent = line.getExtent();
        // Only fit extent on initial load or if user requests? 
        // For now, let's NOT auto-fit every time to avoid annoyance if user zoomed in.
        if (!mapRef.current.get('initialFitDone')) {
          mapRef.current.getView().fit(extent, {
            padding: [80, 80, 80, 80],
            duration: 1500,
            easing: easeOut,
          });
          mapRef.current.set('initialFitDone', true);
        }

      } else {
        // Fallback straight line
        const coords = [
          fromLonLat([pickup.lng, pickup.lat]),
          fromLonLat([delivery.lng, delivery.lat]),
        ];
        if (routeFeatureRef.current) {
          (routeFeatureRef.current.getGeometry() as LineString).setCoordinates(coords);
        } else {
          routeFeatureRef.current = new Feature(new LineString(coords));
          routeFeatureRef.current.setStyle(routeStyle);
          routeSourceRef.current.addFeature(routeFeatureRef.current);
        }

        setRouteStatus({
          loading: false,
          provider: 'Fallback',
          error: 'Using straight-line estimate',
        });
      }
    }, [
      pickup,
      delivery,
      showRealRoute,
      routeStyle,
    ]);

    // Update Route when callback changes (config changes), but don't rebuild map
    useEffect(() => {
      updateRealRoute();
    }, [updateRealRoute]);

    // Initialize map (ONCE)
    useEffect(() => {
      if (!mapContainerRef.current || mapRef.current) return;
      console.log('Initializing map ...');

      const routeLayer = new VectorLayer({
        source: routeSourceRef.current,
        style: routeStyle,
        zIndex: 5,
      });

      const markerLayer = new VectorLayer({
        source: new Cluster({
          distance: 40,
          source: markersSourceRef.current,
        }),
        style: (feature) => {
          const features = feature.get('features') ?? [];
          if (features.length > 1) {
            return new Style({
              image: new Circle({
                radius: 18,
                fill: new Fill({ color: 'rgba(255, 152, 0, 0.9)' }),
                stroke: new Stroke({ color: '#fff', width: 2 }),
              }),
              text: new Text({
                text: features.length.toString(),
                fill: new Fill({ color: '#fff' }),
                font: 'bold 12px sans-serif',
              }),
            });
          }
          // Use the first feature's style
          const originalFeature = features[0];
          return getVehicleStyle(originalFeature);
        },
        zIndex: 10,
      });

      const map = new OLMap({
        target: mapContainerRef.current,
        layers: [
          new TileLayer({
            source: tileSource,
            preload: Infinity, // Preload everything to avoid "picture" effect on zoom
          }),
          routeLayer,
          markerLayer,
        ],
        view: new View({
          center: initialCenter, // Use fine for initial, ignored later
          zoom,
          minZoom: 3,
          maxZoom: 19,
          constrainResolution: true, // Snap to integer zoom for crisp tiles
        }),
        controls: [],
      });

      mapRef.current = map;

      map.once('rendercomplete', () => {
        setLoading(false);
        updateRealRoute(); // Trigger route calculation
      });

      const fallback = setTimeout(() => setLoading(false), 2000);

      return () => {
        clearTimeout(fallback);
        map.setTarget(undefined);
        map.dispose();
        mapRef.current = null;
      };
    }, [tileSource, zoom, getVehicleStyle, routeStyle]); // Minimal dependencies to prevent re-init

    // Update Markers (Vehicles, Pickup, Delivery)
    useEffect(() => {
      // We reuse the single vehicleFeatureMapRef to avoid recreating features if ID exists
      const currentIds = new Set(vehicles.map(v => v.id));
      const source = markersSourceRef.current;
      const featuresToAdd: Feature<Point>[] = [];

      // 1. Update/Add Vehicles
      // 0. Safety check for Map Ref
      if (!(vehicleFeatureMapRef.current instanceof Map)) {
        vehicleFeatureMapRef.current = new Map();
      }

      // 1. Update/Add Vehicles
      vehicles.forEach(v => {
        const coord = fromLonLat([v.lng, v.lat]);
        let feature = vehicleFeatureMapRef.current.get(v.id);

        if (feature) {
          // Update existing
          (feature.getGeometry() as Point).setCoordinates(coord);
        } else {
          // Create new
          feature = new Feature(new Point(coord));
          feature.setId(v.id); // Set ID BEFORE style for caching
          feature.setStyle(getVehicleStyle as any); // OpenLayers allows function as style
          vehicleFeatureMapRef.current.set(v.id, feature);
          featuresToAdd.push(feature);
        }
      });

      // 2. Remove Stale Vehicles
      // 2. Remove Stale Vehicles
      if (vehicleFeatureMapRef.current instanceof Map) {
        for (const [id, feature] of vehicleFeatureMapRef.current.entries()) {
          if (!currentIds.has(id)) {
            source.removeFeature(feature);
            vehicleFeatureMapRef.current.delete(id);
          }
        }
      } else {
        // Recovery for potential HMR/Ref corruption
        console.warn('Vehicle map ref corrupted, resetting');
        vehicleFeatureMapRef.current = new Map();
      }

      // 3. Add new ones to source
      if (featuresToAdd.length > 0) {
        source.addFeatures(featuresToAdd);
      }

      // 4. Ensure Pickup/Delivery Markers are present (Simple clear/add approach for static markers is fine, or check existence)
      // Since they change rarely, we can just ensure they are in the source. 
      // For simplicity, let's just make sure we handle them.
      // Actually, if we clear source, we lose vehicle features. 
      // So we should manage Pickup/Delivery separately or use IDs.

      const pickupId = 'pickup-marker';
      const deliveryId = 'delivery-marker';

      const existingPickup = source.getFeatureById(pickupId);
      if (pickup) {
        const coord = fromLonLat([pickup.lng, pickup.lat]);
        if (existingPickup) {
          (existingPickup.getGeometry() as Point).setCoordinates(coord);
        } else {
          const f = new Feature(new Point(coord));
          f.setId(pickupId);
          f.setStyle(pickupStyle);
          source.addFeature(f);
        }
      } else if (existingPickup) {
        source.removeFeature(existingPickup);
      }

      const existingDelivery = source.getFeatureById(deliveryId);
      if (delivery) {
        const coord = fromLonLat([delivery.lng, delivery.lat]);
        if (existingDelivery) {
          (existingDelivery.getGeometry() as Point).setCoordinates(coord);
        } else {
          const f = new Feature(new Point(coord));
          f.setId(deliveryId);
          f.setStyle(deliveryStyle);
          source.addFeature(f);
        }
      } else if (existingDelivery) {
        source.removeFeature(existingDelivery);
      }

    }, [vehicles, pickup, delivery, getVehicleStyle, pickupStyle, deliveryStyle]);

    return (
      <MapErrorBoundary>
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height,
            position: 'relative',
            borderRadius: '12px',
            overflow: 'hidden',
            background: darkMode ? '#1a1a2e' : '#f0f0f0',
          }}
          role="region"
          aria-label="Live delivery tracking map"
        >
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: darkMode ? '#1a1a2e' : '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                color: darkMode ? '#aaa' : '#666',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛰️</div>
                <div>Initializing map ...</div>
              </div>
            </div>
          )}

          {/* Reuse existing overlay styles for routing status */}
          {!routeStatus.loading && routeStatus.distance && (
            <div
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: darkMode
                  ? 'rgba(26, 26, 46, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
                color: darkMode ? '#fff' : '#333',
                padding: '12px 16px',
                borderRadius: '8px',
                zIndex: 20,
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(0, 0, 0,0.15)',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {routeStatus.distance} {routeStatus.duration}
              </div>
            </div>
          )}
        </div>
      </MapErrorBoundary>
    );
  },
);

LiveMap.displayName = 'LiveMap';
export default LiveMap;
